import DatabaseModel from './DatabaseModel.js';
import { unwrapDto } from './responseUtils.js';
import RhSolicitudesVacaciones, {
  escrituraDelModulo
} from './RhSolicitudesVacaciones.js';

/**
 * Las llamadas de las pantallas del **aprobador** (#59): la bandeja «solicitudes de mi equipo»,
 * el badge del menú, de quién es uno aprobador, y aprobar y denegar.
 *
 * Está aparte de `RhSolicitudesVacaciones.js` —las pantallas del solicitante (#58)— por el mismo
 * motivo por el que aquella está aparte de `RhVacaciones.js`: son pantallas de personas distintas
 * y, metidas en el mismo fichero, no se sabe qué usa quién.
 *
 * Aquí **no se decide nada**. Quién puede aprobar a quién, qué va en cada pestaña y qué contadores
 * salen lo resuelve el servidor y llega hecho; esta pantalla solo pide y pinta. Si un contador no
 * cuadra, se arregla allí.
 */
const RhBandejaVacaciones = Object.create(DatabaseModel);

RhBandejaVacaciones.model = 'rh/bandeja-vacaciones';
RhBandejaVacaciones.modelFront = 'vacaciones';

/** La raíz de los endpoints de la #57, que son los que aprueban y deniegan. */
const SOLICITUDES = 'rh/vacaciones';

/**
 * #59 · **Si un rechazo no hay que contárselo a nadie** —el intento repetido de algo que ya está en
 * marcha, o la respuesta de una sesión que ya se fue—.
 *
 * Es la misma pregunta que ya tenía el adaptador del solicitante y se reexpone aquí para que las
 * pantallas del aprobador no tengan que importar el otro adaptador para hacerla: las dos escrituras
 * de aquí pasan por las mismas garantías, así que pueden recibir los mismos rechazos mudos.
 */
RhBandejaVacaciones.sinNadaQueDecir = RhSolicitudesVacaciones.sinNadaQueDecir;

/**
 * #59 · El nombre del aviso del `EventBus` que se manda cuando esta sesión acaba de resolver una
 * solicitud. Vive aquí, junto a las dos llamadas que lo provocan, para que quien añada una tercera
 * forma de resolver (las acciones de anulaciones son de la #69) lo encuentre sin buscarlo.
 *
 * Lo escucha el **menú lateral**, que se monta una sola vez en su vista persistente y por tanto no
 * se vuelve a enterar de nada: sin este aviso su badge se quedaba con el número de la sesión
 * entera, incluso después de que no quedara ninguna solicitud por resolver.
 *
 * Lo mandan las pantallas, no este adaptador: aquí no se decide nada y lo que pasa después de una
 * resolución es cosa de quien la ha pedido.
 */
export const EVENTO_VACACIONES_RESUELTA = 'vacaciones:resuelta';

// GET /movilidad/rh/bandeja-vacaciones[?pestana=PENDIENTES&anio=N]
// → { username, pestana, anio, apruebaAAlguien, todaLaPlantilla, personas[], solicitudes[],
//     pendientes, anulaciones, confirmadas, todas, badge,
//     porAnio: { anios: [{ anio, pendientes, anulaciones, badge }], badge } }   (#1119)
//
// Las filas de la pestaña y los contadores de las cuatro vienen en la MISMA respuesta: pedirlos
// por separado puede devolver fotos distintas —basta con que entre las dos se apruebe algo— y
// dejaría «Pendientes (4)» encima de tres filas.
RhBandejaVacaciones.bandeja = function(pestana, anio) {
    const self = this;
    const params = [];
    if (pestana) params.push('pestana=' + encodeURIComponent(pestana));
    if (anio) params.push('anio=' + anio);
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + (params.length ? '?' + params.join('&') : ''),
        method: 'GET'
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/bandeja-vacaciones/pendientes-por-anio
// → { anios: [{ anio, pendientes, anulaciones, badge }], badge }
//
// #1119 · Lo que le espera al aprobador en el año en curso y en el siguiente. La bandeja es de UN
// año, y pedida sin año dejaba fuera las vacaciones de enero pedidas en octubre: llegaba el correo
// y el contador del menú no las contaba. Es lo que pide el menú; la bandeja trae lo mismo en su
// `porAnio`, de la misma cuenta, para que el selector de año y el menú digan lo mismo.
RhBandejaVacaciones.pendientesPorAnio = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/pendientes-por-anio',
        method: 'GET'
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/bandeja-vacaciones/mi-equipo
// → { username, todaLaPlantilla, personas[], apruebaAAlguien }
//
// De quién es uno aprobador o suplente. Lo usa el selector «Empleado» del calendario, que tiene
// que ofrecer SOLO a esas personas: el servidor no deja ver el calendario de nadie más, así que
// ofrecer a otro acabaría en una rejilla en blanco sin decir por qué.
RhBandejaVacaciones.miEquipo = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mi-equipo',
        method: 'GET'
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/bandeja-vacaciones/dia?username=X&fecha=YYYY-MM-DD
// → la solicitud de ese día de ESA persona, o null.
//
// Es el hermano de `RhSolicitudesVacaciones.solicitudDelDia`, que contesta siempre con las del
// usuario logueado: en el calendario de otra persona aquel devolvía una solicitud propia que
// coincidiera en la fecha. Este recibe de quién es el calendario, y el servidor comprueba que se
// pueda mirar: un 403 aquí es un 403, no un «no hay nada ese día».
RhBandejaVacaciones.solicitudDelDia = function(username, fecha) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/dia?username='
            + encodeURIComponent(username) + '&fecha=' + encodeURIComponent(fecha),
        method: 'GET'
    }, false).then(unwrapDto).then(function(solicitud) {
        // Mismo cuidado que en la #58: `unwrapDto` solo reconoce el envoltorio si le llegan al
        // menos dos de sus campos, y con la inclusión NON_NULL de producción una respuesta sin
        // solicitud se queda en `{ list: [] }` —un objeto, y por tanto algo que la pantalla
        // pintaría como si fuera una solicitud sin código ni estado—. La marca de que hay
        // solicitud es su `codigo`.
        return solicitud && solicitud.codigo ? solicitud : null;
    });
};

// POST /movilidad/rh/vacaciones/solicitudes/{id}/aprobar → la solicitud ya aprobada.
//
// Es el endpoint de la #57 y se llama tal cual: aprobar desde la bandeja y aprobar desde el
// calendario son la misma acción sobre la misma solicitud, no dos. La `version` es la que traía
// la solicitud al pintarla, y es lo que corta dos resoluciones a la vez.
RhBandejaVacaciones.aprobar = function(id, version, motivo) {
    return resolver(this, id, 'aprobar', version, motivo);
};

// POST /movilidad/rh/vacaciones/solicitudes/{id}/denegar → la solicitud ya denegada.
// El motivo es OBLIGATORIO y lo exige el servidor; la pantalla lo pide antes para no gastar un
// viaje, pero quien manda es él.
RhBandejaVacaciones.denegar = function(id, version, motivo) {
    return resolver(this, id, 'denegar', version, motivo);
};

function resolver(self, id, accion, version, motivo) {
    // **Una resolución a la vez por solicitud, y atada a la sesión que la lanzó.** Las dos
    // garantías son las del módulo y viven en un solo sitio (`RhSolicitudesVacaciones`), que es de
    // donde se importan: estas dos escrituras salieron sin pasar por ellas y su única defensa
    // contra el doble toque era el cerrojo de la pantalla, que es justo lo que no dura lo que dura
    // el POST —tres rondas de revisión el 17/10/2026, una por cada sitio donde se perdía—.
    //
    // La clave es `resolver|<id>` y no `aprobar|<id>`: lo que no puede salir dos veces es una
    // resolución de esa solicitud, y mandar «aprobar» y «denegar» a la vez con la misma `version`
    // es el mismo daño con dos verbos. Resolver dos solicitudes distintas a la vez sí es legítimo.
    return escrituraDelModulo('resolver|' + id, function() {
        return self.sendRequest({
            url: self.server_movilidad + self.prefix + SOLICITUDES + '/solicitudes/'
                + encodeURIComponent(id) + '/' + accion,
            method: 'POST',
            data: { version: version, motivo: motivo || null }
        }, false).then(unwrapDto);
    });
}

// --- #69 · las acciones de la pestaña «Anulaciones» y la anulación directa -----------------------
//
// Las tres salen del MISMO endpoint que ya usaban aprobar y denegar (`rh/vacaciones`), y se llaman
// tal cual: resolver desde la bandeja y resolver desde el calendario de la persona son la misma
// acción sobre la misma solicitud, no dos. La `version` es la que traía la solicitud al pintarla y
// es lo que corta dos resoluciones a la vez.

// POST /movilidad/rh/vacaciones/solicitudes/{id}/anulacion/aceptar → la solicitud ya anulada.
// Los días de la petición se borran de la base de datos y vuelven al saldo; los que no estaban en
// la petición siguen confirmados. El motivo es opcional al aceptar.
RhBandejaVacaciones.aceptarAnulacion = function(id, version, motivo) {
    return resolver(this, id, 'anulacion/aceptar', version, motivo);
};

// POST /movilidad/rh/vacaciones/solicitudes/{id}/anulacion/rechazar → la solicitud sin cambios.
// El motivo es OBLIGATORIO y lo exige el servidor; la pantalla lo pide antes para no gastar un
// viaje, pero quien manda es él.
RhBandejaVacaciones.rechazarAnulacion = function(id, version, motivo) {
    return resolver(this, id, 'anulacion/rechazar', version, motivo);
};

// POST /movilidad/rh/vacaciones/solicitudes/{id}/anular-directamente → la solicitud ya anulada.
//
// Sin petición previa: días futuros, **tipo** de la lista cerrada y **motivo**, los dos
// obligatorios. No lleva ningún campo de diagnóstico, tampoco con «incapacidad temporal»: el tipo
// es lo único que queda, y añadirlo aquí sería mandar un dato de salud que nadie ha pedido.
//
// Por la puerta del módulo y con la MISMA clave que las otras cuatro resoluciones
// (`resolver|<id>`): anular directamente es una resolución de esa solicitud con su `version`, así
// que no puede salir a la vez que un «aceptar la anulación» de la misma fila.
RhBandejaVacaciones.anularDirectamente = function(id, version, dias, tipo, motivo) {
    const self = this;
    return escrituraDelModulo('resolver|' + id, function() {
        return self.sendRequest({
            url: self.server_movilidad + self.prefix + SOLICITUDES + '/solicitudes/'
                + encodeURIComponent(id) + '/anular-directamente',
            method: 'POST',
            data: { version: version, dias: dias, tipo: tipo, motivo: motivo }
        }, false).then(unwrapDto);
    });
};

// GET /movilidad/rh/vacaciones/anulacion/tipos → [{ codigo, etiqueta }, …]
//
// La lista de tipos la sirve el servidor y no la escribe esta pantalla: es una lista cerrada, y
// teniéndola en los dos lados se separan. Si mañana se añade un tipo, el desplegable lo ofrece sin
// tocar el front.
RhBandejaVacaciones.tiposAnulacion = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + SOLICITUDES + '/anulacion/tipos',
        method: 'GET'
    }, false).then(unwrapDto).then(function(respuesta) {
        // `ResponseDto` de una lista llega como `{ list: [...] }`; `unwrapDto` deja el objeto
        // entero cuando no reconoce el envoltorio. Lo que esta función promete es el array.
        if (Array.isArray(respuesta)) return respuesta;
        return (respuesta && Array.isArray(respuesta.list)) ? respuesta.list : [];
    });
};

export default RhBandejaVacaciones;
